import type { Partner } from "@expert/shared-types";

import { useEffect, useRef, useState } from "react";

import { getStopwatchElapsedSeconds } from "@/shared-utils/hooks/useStopwatch";

import { getCallCategoryDisplayName } from "./callCategoryDisplayNames";
import { useCallCategoryStore } from "./callCategoryStore";
import { emitPacingThresholdCrossed } from "./pacingAnalytics";
import { usePacingTarget } from "./usePacingTarget";

export type PacingVisualState = "neutral" | "onTrack" | "approaching" | "exceeded";

export interface PacingState {
    enabled: boolean;
    hasTarget: boolean;
    state: PacingVisualState;
    elapsedSeconds: number;
    targetSeconds: number;
    progressPercent: number;
    overageSeconds: number;
    displayCategory: string | null;
    salesEligible: boolean;
    salesEligibleBufferSeconds: number;
}

function derivePacingState(ratio: number, approachingThreshold: number, exceededThreshold: number): PacingVisualState {
    if (ratio >= exceededThreshold) return "exceeded";
    if (ratio >= approachingThreshold) return "approaching";
    return "onTrack";
}

export interface UsePacingStateOptions {
    partner: Partner;
    sessionId: string;
    startedTimestamp: number | undefined;
    callActive: boolean;
    /**
     * Whether pacing is active for this expert (combined feature flag + partner config gate).
     * Typically derived via {@link useIsPacingActive}. Defaults to true so callers that have
     * already verified the gate upstream don't need to re-pass it.
     */
    isPacingActive?: boolean;
    /**
     * Whether this hook instance should fire pacing threshold analytics events. Set to false
     * for secondary subscribers (e.g. the bar outline) so multiple components reading the
     * same pacing state do not double-fire events.
     */
    emitAnalytics?: boolean;
}

export function usePacingState({
    partner,
    sessionId,
    startedTimestamp,
    callActive,
    isPacingActive = true,
    emitAnalytics = true,
}: UsePacingStateOptions): PacingState {
    const pacing = usePacingTarget(partner, sessionId);
    const enabled = pacing.enabled && isPacingActive;
    const callCategory = useCallCategoryStore((s) => s.callCategory);
    const [elapsedSeconds, setElapsedSeconds] = useState(() => getStopwatchElapsedSeconds(startedTimestamp));
    const thresholdsFired = useRef({ approaching: false, exceeded: false });

    // Reset thresholds when session or target changes
    useEffect(() => {
        thresholdsFired.current = { approaching: false, exceeded: false };
    }, [sessionId, pacing.targetSeconds]);

    // Sync elapsed time immediately when startedTimestamp changes (e.g. task loads after refresh)
    useEffect(() => {
        setElapsedSeconds(getStopwatchElapsedSeconds(startedTimestamp));
    }, [startedTimestamp]);

    // Tick every second while active and pacing is enabled
    useEffect(() => {
        if (!callActive || !enabled) return;
        const interval = setInterval(() => {
            setElapsedSeconds(getStopwatchElapsedSeconds(startedTimestamp));
        }, 1000);
        return () => clearInterval(interval);
    }, [startedTimestamp, callActive, enabled]);

    // Threshold analytics — only the primary subscriber emits these so multiple components reading
    // the same pacing state (e.g. the pill and the bar outline) do not double-fire events.
    useEffect(() => {
        if (!enabled || !emitAnalytics || pacing.targetSeconds === 0) return;
        const ratio = elapsedSeconds / pacing.targetSeconds;

        if (ratio >= pacing.approachingThreshold && !thresholdsFired.current.approaching) {
            thresholdsFired.current.approaching = true;
            emitPacingThresholdCrossed({
                threshold: "approaching",
                elapsedSeconds,
                targetSeconds: pacing.targetSeconds,
                gaiaCallCategory: pacing.callCategory,
            });
        }
        if (ratio >= pacing.exceededThreshold && !thresholdsFired.current.exceeded) {
            thresholdsFired.current.exceeded = true;
            emitPacingThresholdCrossed({
                threshold: "exceeded",
                elapsedSeconds,
                targetSeconds: pacing.targetSeconds,
                gaiaCallCategory: pacing.callCategory,
            });
        }
    }, [
        elapsedSeconds,
        enabled,
        emitAnalytics,
        pacing.targetSeconds,
        pacing.callCategory,
        pacing.approachingThreshold,
        pacing.exceededThreshold,
    ]);

    const hasTarget = enabled && pacing.targetSeconds > 0;

    const state: PacingVisualState = !hasTarget
        ? "neutral"
        : derivePacingState(
              elapsedSeconds / pacing.targetSeconds,
              pacing.approachingThreshold,
              pacing.exceededThreshold,
          );

    return {
        enabled,
        hasTarget,
        state,
        elapsedSeconds,
        targetSeconds: pacing.targetSeconds,
        progressPercent: hasTarget ? Math.min((elapsedSeconds / pacing.targetSeconds) * 100, 100) : 0,
        overageSeconds: hasTarget ? Math.max(elapsedSeconds - pacing.targetSeconds, 0) : 0,
        displayCategory: callCategory ? getCallCategoryDisplayName(callCategory) : null,
        salesEligible: pacing.salesEligible,
        salesEligibleBufferSeconds: pacing.salesEligibleBufferSeconds,
    };
}
